import { useState, useEffect, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { KATEGORI_FARGER, basPassNamn } from '../../lib/constants'
import { BekraftaModal } from '../BekraftaModal'

export function RedigeraPassModal({ pass, passtyper, avdelning, allaMedarbetarePass, onSave, onClose }) {
  const { medarbetare: adminInfo } = useAuth()
  const [valdPasstyp, setValdPasstyp] = useState(pass?.passtyp_id || '')
  const [valdKategori, setValdKategori] = useState(
    pass?.kategori === 'overtid' ? 'ordinarie' : (pass?.kategori || 'ordinarie')
  )
  const [erOvertid, setErOvertid] = useState(pass?.kategori === 'overtid')
  const [harAtf, setHarAtf] = useState(pass?.har_atf || false)
  const [harBeredskap, setHarBeredskap] = useState(pass?.har_beredskap || false)
  const [harBokaDag, setHarBokaDag] = useState(pass?.har_boka_dag || false)
  const [atfFran, setAtfFran] = useState(pass?.atf_fran?.substring(0,5) || '')
  const [atfTill, setAtfTill] = useState(pass?.atf_till?.substring(0,5) || '')
  const [passtypAndrat, setPasstypAndrat] = useState(false)
  const [statusAndrat, setStatusAndrat] = useState(false)

  // Drag-funktionalitet för modalen
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const dragging = useRef(false)
  const dragStart = useRef({ x: 0, y: 0 })

  function startaDrag(e) {
    dragging.current = true
    dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }
  }

  useEffect(() => {
    function paMove(e) {
      if (!dragging.current) return
      setPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y })
    }
    function paUpp() { dragging.current = false }
    window.addEventListener('mousemove', paMove)
    window.addEventListener('mouseup', paUpp)
    return () => {
      window.removeEventListener('mousemove', paMove)
      window.removeEventListener('mouseup', paUpp)
    }
  }, [])
  const [viloVarning, setViloVarning] = useState(null)
  const [kommentar, setKommentar] = useState('')
  const [andringsdatum, setAndringsdatum] = useState('')
  // Lokalt datum (inte UTC) - ändringsdatum får inte ligga i framtiden
  const idagStr = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}` })()
  const andringsdatumFramtid = !!andringsdatum && andringsdatum > idagStr
  const [gammalPassManuell, setGammalPassManuell] = useState('')
  const [synligNotering, setSynligNotering] = useState(pass?.notering || '')
  const [historik, setHistorik] = useState([])
  const [loading, setLoading] = useState(false)
  const overlayMouseDownRef = useRef(false)
  const [visaTaBortDialog, setVisaTaBortDialog] = useState(false)
  const [angraRad, setAngraRad] = useState(null) // historikrad som ska ångras
  const [gulKoppling, setGulKoppling] = useState(null) // pass som är gulmarkerat av en väntande ansökan
  const [visaAngraGul, setVisaAngraGul] = useState(false)
  const [visaIngenVila, setVisaIngenVila] = useState(false)
  const [xFel, setXFel] = useState('')
  const arXdag = !!pass?.id && !pass?._multimarkerade?.length && ['X', 'SX', 'LX', 'SJX'].includes((pass?.passtyp_namn || '').toUpperCase().trim())
  const arVeckovila = !!pass?.id && !pass?._multimarkerade?.length && ['V', 'SV', 'LV'].includes((pass?.passtyp_namn || '').toUpperCase().trim())

  useEffect(() => {
    if (pass?.id) hamtaHistorik()
  }, [pass?.id])

  async function hamtaHistorik() {
    const { data } = await supabase
      .from('pass_historik').select('*').eq('pass_id', pass.id)
      .order('andrad_at', { ascending: false })
    setHistorik(data || [])
  }

  async function kontrolleraVila() {
    const medId = pass.medarbetare_id || pass._multimarkerade?.[0]?.medarbetare_id
    if (!medId || !pass.datum) return null

    const { data: allPass } = await supabase
      .from('pass')
      .select('datum, passtyp_namn, kategori')
      .eq('medarbetare_id', medId)
      .order('datum', { ascending: true })

    if (!allPass?.length) return null

    const { data: passtyperData } = await supabase
      .from('passtyper').select('namn, starttid, sluttid')

    // Lägg till det nya passet
    const nyKat = erOvertid ? 'overtid' : valdKategori
    const nyNamn = passtyper.find(p => p.id === valdPasstyp)?.namn || pass.passtyp_namn || ''
    const passMap = {}
    for (const p of allPass) passMap[p.datum] = p
    passMap[pass.datum] = { datum: pass.datum, passtyp_namn: nyNamn, kategori: nyKat }

    function erJobbdag(passtyp_namn, kategori) {
      const n = (passtyp_namn || '').toUpperCase().trim()
      if (kategori === 'beredskap') return true
      if (!n) return false
      if (['X','SX','LX','SJX','V'].includes(n)) return false
      if (n.startsWith('L') || n.startsWith('S')) return false
      if (['ledig','semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','overtid'].includes(kategori)) return false
      return true
    }

    function normTid(t) { return t ? t.substring(0, 5) : null }
    function tidMin(t) {
      if (!t) return null
      const [h, m] = t.split(':').map(Number)
      return h * 60 + m
    }
    function getTider(pNamn) {
      let bas = (pNamn || '').toUpperCase().trim()
      bas = bas.replace(/^OT\s*/i,'').trim()
      if (bas.startsWith('SJ') && bas.length > 2) bas = bas.slice(2)
      else if (bas.startsWith('S') && bas.length > 1) bas = bas.slice(1)
      if (bas.startsWith('L') && bas.length > 1) bas = bas.slice(1)
      const pt = passtyperData?.find(p => p.namn?.toUpperCase() === bas)
      return pt ? { start: normTid(pt.starttid), slut: normTid(pt.sluttid) } : null
    }

    const varningar = []
    const alla = Object.values(passMap).sort((a,b) => a.datum.localeCompare(b.datum))
    const forstaDag = alla[0].datum
    const sistaDag = alla[alla.length-1].datum

    // --- DYGNSVILA ---
    // Kontrollera bara mellan pass som ligger max 2 dagar från varandra
    const jobbaDagar = alla.filter(p => erJobbdag(p.passtyp_namn, p.kategori))
    for (let i = 0; i < jobbaDagar.length - 1; i++) {
      const a = jobbaDagar[i], b = jobbaDagar[i+1]
      const dagarMellan = (new Date(b.datum+'T12:00:00') - new Date(a.datum+'T12:00:00')) / (1000*60*60*24)
      // Om mer än 2 dagar mellanrum = ledig dag emellan = ingen dygnsvila-kontroll
      if (dagarMellan > 2) continue
      const tA = getTider(a.passtyp_namn), tB = getTider(b.passtyp_namn)
      if (!tA || !tB) continue
      const slutMin = tidMin(tA.slut)
      const startMin = tidMin(tA.start)
      const erNatt = slutMin < startMin
      const slutJust = erNatt ? slutMin + 1440 : slutMin
      const vilaMin = (dagarMellan * 1440) - slutJust + tidMin(tB.start)
      if (vilaMin < 660 && (a.datum === pass.datum || b.datum === pass.datum)) {
        varningar.push(`Dygnsvila: ${a.datum} → ${b.datum} (${Math.round(vilaMin/60*10)/10}h, krav 11h)`)
      }
    }

    // --- VECKOVILA: max 11 jobbdagar i sträck, sedan 3 lediga dagar ---
    let streak = 0, dag11Datum = null, streakStart = null
    let d = new Date(forstaDag + 'T12:00:00')
    const slut = new Date(sistaDag + 'T12:00:00')

    while (d <= slut) {
      const dat = d.toISOString().split('T')[0]
      const p = passMap[dat]
      const jobbdag = p ? erJobbdag(p.passtyp_namn, p.kategori) : false

      if (jobbdag) {
        if (streak === 0) streakStart = dat
        streak++
        if (streak === 11) dag11Datum = dat
        if (streak >= 12) {
          if (pass.datum >= streakStart && pass.datum <= dat) {
            varningar.push(`Veckovila: ${streak} jobbdagar i sträck (max 11)`)
          }
          break
        }
      } else {
        streak = 0
        streakStart = null
        if (dag11Datum) {
          const lediga = (d - new Date(dag11Datum + 'T12:00:00')) / (1000*60*60*24)
          if (lediga >= 3) dag11Datum = null
        }
      }

      // Om detta är det nya passet och dag11 fortfarande aktiv
      if (dat === pass.datum && dag11Datum && jobbdag) {
        const dEfter = (new Date(dat+'T12:00:00') - new Date(dag11Datum+'T12:00:00')) / (1000*60*60*24)
        if (dEfter >= 1 && dEfter <= 3) {
          varningar.push(`Veckovila: Bara ${dEfter} ledig dag efter 11-dagars sträck – 72h vila krävs (minst 3 dagar)`)
        }
      }

      d.setDate(d.getDate() + 1)
    }

    return varningar.length > 0 ? varningar : null
  }


  async function handleSpara() {
    if (andringsdatumFramtid) return
    setLoading(true)
    try {
      const varningar = await kontrolleraVila()
      if (varningar) {
        setViloVarning(varningar)
        setLoading(false)
        return
      }
      await genomforSpara()
    } catch (err) {
      console.error('Fel:', err)
      alert('Fel: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  async function genomforSpara() {
    try {
      const multipass = pass._multimarkerade
      if (multipass?.length > 0) {
        for (const mp of multipass) await sparaEttPass(mp)
        onSave(); return
      }
      await sparaEttPass(pass)
      onSave()
    } catch (err) {
      console.error('Fel:', err)
      alert('Fel: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  async function sparaEttPass(ettPass) {
    const nyPasstyp = passtyper.find(p => p.id === valdPasstyp)
    const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
    const passTypByttes = passtypAndrat
    const slutligKategori = erOvertid ? 'overtid' : valdKategori

    let basNamn = passTypByttes ? (nyPasstyp?.namn || ettPass.passtyp_namn) : ettPass.passtyp_namn
    let nyttaTimmar = passTypByttes ? (nyPasstyp?.arbetstimmar || ettPass.arbetstimmar || 0) : (ettPass.arbetstimmar || 0)
    let nyttPassNamn = basNamn || ''

    if (erOvertid) {
      nyttPassNamn = 'ÖT ' + nyttPassNamn.replace(/^ÖT\s*/i,'').trim()
      nyttaTimmar = 0
    } else if (['semester','beviljad_semester'].includes(slutligKategori)) {
      // Ta bort ett ev. redan befintligt S/L-prefix innan det nya läggs på,
      // annars byggs det på för varje gång man byter kategori (LF → SLF →
      // LSLF osv).
      if (!passTypByttes && /^[SL][A-ZÅÄÖ0-9]/.test(nyttPassNamn.toUpperCase()) && nyttPassNamn.length > 1) {
        nyttPassNamn = nyttPassNamn.slice(1)
      }
      nyttPassNamn = 'S' + nyttPassNamn
    } else if (['foraldraledighet','beviljad_foraldraledighet','permission','flex','ledig'].includes(slutligKategori)) {
      if (!passTypByttes && /^[SL][A-ZÅÄÖ0-9]/.test(nyttPassNamn.toUpperCase()) && nyttPassNamn.length > 1) {
        nyttPassNamn = nyttPassNamn.slice(1)
      }
      nyttPassNamn = 'L' + nyttPassNamn
    } else if (slutligKategori === 'sjukdom') {
      // Sjukdom: bara en beteckning ("Sj") framför passet - timmarna ska INTE
      // nollställas (behålls för lönesyfte), men prefixet gör att passet inte
      // räknas som bemannat i bemanningsöversikten längst ner.
      if (!nyttPassNamn.toUpperCase().startsWith('SJ')) nyttPassNamn = 'Sj' + nyttPassNamn
    }

    const erSemesterKategori = ['semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','permission','flex'].includes(slutligKategori)
    // En stjärna som redan finns (t.ex. från ett passbyte eller en tidigare
    // ändring) ska ALDRIG försvinna bara för att någon senare väljer
    // semester/sjukdom osv för samma dag - man kan ju ha bytt eller fått
    // passet ändrat FÖRST och sökt semester EFTERÅT. Semester/sjukdom i sig
    // ska bara inte skapa en NY stjärna på egen hand.
    const markeraSomPassandring = !!ettPass.har_passandring || (
      (erSemesterKategori || slutligKategori === 'sjukdom')
        ? false
        : (passtypAndrat || statusAndrat || !!andringsdatum)
    )

    let passError = null, nyttPassId = ettPass.id
    const sparadPasstypId = passtypAndrat ? (valdPasstyp || null) : (ettPass.passtyp_id || null)
    if (ettPass.id) {
      const { error } = await supabase.from('pass').update({
        passtyp_id: sparadPasstypId,
        passtyp_namn: nyttPassNamn,
        kategori: slutligKategori,
        arbetstimmar: nyttaTimmar,
        har_passandring: markeraSomPassandring,
        har_atf: harAtf,
        har_beredskap: harBeredskap,
        har_boka_dag: harBokaDag,
        atf_fran: harAtf ? (atfFran || null) : null,
        atf_till: harAtf ? (atfTill || null) : null,
        notering: synligNotering || null,
        visa_notering_i_schema: !!synligNotering.trim(),
        uppdaterad_at: new Date().toISOString()
      }).eq('id', ettPass.id)
      passError = error
    } else {
      const { data: nyttPass, error } = await supabase.from('pass').insert({
        medarbetare_id: ettPass.medarbetare_id,
        datum: ettPass.datum,
        passtyp_id: sparadPasstypId,
        passtyp_namn: nyttPassNamn,
        kategori: slutligKategori,
        arbetstimmar: nyttaTimmar,
        har_passandring: markeraSomPassandring,
        har_atf: harAtf,
        har_beredskap: harBeredskap,
        har_boka_dag: harBokaDag,
        atf_fran: harAtf ? (atfFran || null) : null,
        atf_till: harAtf ? (atfTill || null) : null,
        notering: synligNotering || null,
        visa_notering_i_schema: !!synligNotering.trim(),
      }).select().single()
      passError = error
      if (nyttPass) nyttPassId = nyttPass.id
    }
    if (passError) throw passError

    if (nyttPassId) {
      const atfNotering = harAtf && atfFran && atfTill
        ? `ATF mellan ${atfFran.replace(':', '.')}-${atfTill.replace(':', '.')}`
        : null
      const synligNoteringAndrad = (synligNotering || '') !== (ettPass.notering || '')
      const schemaNotering = synligNoteringAndrad
        ? (synligNotering ? `Notering i schema: "${synligNotering}"` : 'Notering i schema borttagen')
        : null
      const helaNoteringen = [kommentar || null, schemaNotering, atfNotering].filter(Boolean).join(' · ') || null
      await supabase.from('pass_historik').insert({
        pass_id: nyttPassId,
        andrad_av: adminInfo.id,
        andrad_av_namn: adminNamn,
        gammal_passtyp: (andringsdatum && gammalPassManuell) ? gammalPassManuell : (ettPass.passtyp_namn || ''),
        ny_passtyp: nyttPassNamn,
        gammal_kategori: ettPass.kategori || 'ordinarie',
        ny_kategori: slutligKategori,
        notering: helaNoteringen,
        andrad_at: andringsdatum ? `${andringsdatum}T12:00:00` : new Date().toISOString(),
      })
    }

    const gamlaTimmar = (!ettPass.id || ettPass.kategori === 'overtid') ? 0 : (ettPass.arbetstimmar || 0)
    const nyaTimmar = slutligKategori === 'overtid' ? 0 : nyttaTimmar
    const diff = nyaTimmar - gamlaTimmar
    await justeraXOchSX(ettPass.medarbetare_id, -diff, ettPass.datum) // räknar om X-dagen från periodens krav

    // Skicka notis till medarbetaren i deras inkorg
    const datumFormaterat = new Date(ettPass.datum).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })
    const gamaltPass = ettPass.passtyp_namn || '–'
    const nyttPassVisning = nyttPassNamn || '–'
    // Skicka bara notis om passnamnet faktiskt ändrats (inte bara kategori)
    const harAndrats = gamaltPass !== nyttPassVisning

    if (harAndrats && ettPass.medarbetare_id) {
      await supabase.from('ansokningar').insert({
        typ: 'passandring_notis',
        skapad_av: adminInfo.id,
        skapad_av_namn: adminNamn,
        avdelning: null,
        fran_datum: ettPass.datum,
        till_datum: ettPass.datum,
        status: 'beviljad',
        medarbetare_kommentar: null,
        admin_kommentar: kommentar || null,
        besvarad_av: adminInfo.id,
        besvarad_av_namn: adminNamn,
        besvarad_at: new Date().toISOString(),
        // Spara på medarbetarens ID som skapad_av så det hamnar i deras inkorg
        mottagare_id: ettPass.medarbetare_id,
        passandring_fran: gamaltPass,
        passandring_till: nyttPassVisning,
        passandring_datum: ettPass.datum
      })
    }
  }

  // ── Ångra senaste ändringen ──
  // Sätter tillbaka passet till det det var före ändringen och tar bort loggraden,
  // så att ev. skiftersättning/stjärna som hörde till just den ändringen försvinner.
  const KAT_UTAN_STJARNA = ['semester','beviljad_semester','foraldraledighet','beviljad_foraldraledighet','permission','flex','sjukdom']
  const arRiktigAndring = h => (h.gammal_passtyp !== h.ny_passtyp || h.gammal_kategori !== h.ny_kategori) && (!!h.gammal_passtyp || !!h.ny_passtyp)
  const senasteAndring = (!pass?._multimarkerade?.length) ? historik.find(arRiktigAndring) : null

  async function angraSenasteAndring(h) {
    setLoading(true)
    try {
      const { data: nu, error: hamtFel } = await supabase.from('pass').select('*').eq('id', pass.id).single()
      if (hamtFel || !nu) throw hamtFel || new Error('Hittade inte passet')

      // Passet lades på en dag som var ledig (t.ex. övertid): ta bort det helt så dagen blir ledig igen
      if (!h.gammal_passtyp) {
        await supabase.from('pass_historik').delete().eq('pass_id', pass.id)
        const { data: borta } = await supabase.from('pass').delete().eq('id', pass.id).select('id')
        if (!borta || borta.length === 0) {
          // Fick inte radera: töm passet i stället
          const { error: tomFel } = await supabase.from('pass').update({
            passtyp_id: null, passtyp_namn: '', kategori: 'ordinarie', arbetstimmar: 0,
            har_passandring: false, har_atf: false, har_beredskap: false, har_boka_dag: false,
            atf_fran: null, atf_till: null, notering: null, visa_notering_i_schema: false,
            gul_ansokan_id: null, gul_ursprung_namn: null, gul_ursprung_kategori: null, gul_nytt: false,
            uppdaterad_at: new Date().toISOString()
          }).eq('id', pass.id)
          if (tomFel) throw tomFel
        }
        await justeraXOchSX(nu.medarbetare_id, 0, nu.datum)
        if (nu.medarbetare_id && (nu.passtyp_namn || '').trim()) {
          const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
          await supabase.from('ansokningar').insert({
            typ: 'passandring_notis', skapad_av: adminInfo.id, skapad_av_namn: adminNamn, avdelning: null,
            fran_datum: nu.datum, till_datum: nu.datum, status: 'beviljad',
            medarbetare_kommentar: null, admin_kommentar: 'Ändringen har ångrats.',
            besvarad_av: adminInfo.id, besvarad_av_namn: adminNamn, besvarad_at: new Date().toISOString(),
            mottagare_id: nu.medarbetare_id, passandring_fran: nu.passtyp_namn || '–', passandring_till: 'Ledig', passandring_datum: nu.datum
          })
        }
        onSave()
        return
      }

      const aterNamn = h.gammal_passtyp
      const aterKategori = h.gammal_kategori || 'ordinarie'
      const utanOt = aterNamn.replace(/^ÖT\s*/i, '').trim()
      const matchad = passtyper.find(p => (p.namn || '').toUpperCase() === aterNamn.toUpperCase())
        || passtyper.find(p => (p.namn || '').toUpperCase() === (basPassNamn(utanOt) || '').toUpperCase())
        || passtyper.find(p => (p.namn || '').toUpperCase() === utanOt.toUpperCase())

      // Timmar: övertid = 0. Byter passet grundtyp hämtas timmarna från den återställda passtypen,
      // annars behålls de som de är (kategoribyten ändrar inte timmarna).
      const nuBas = (basPassNamn((nu.passtyp_namn || '').replace(/^ÖT\s*/i, '').trim()) || '').toUpperCase()
      const aterBas = (basPassNamn(utanOt) || '').toUpperCase()
      let aterTimmar = nu.arbetstimmar || 0
      if (aterKategori === 'overtid') aterTimmar = 0
      else if (['X','SX','LX','SJX'].includes(aterNamn.toUpperCase().trim())) aterTimmar = nu.arbetstimmar || 0
      else if (nu.kategori === 'overtid' || nuBas !== aterBas) aterTimmar = matchad?.arbetstimmar ?? (nu.arbetstimmar || 0)

      // Stjärna/ersättning ska bara finnas kvar om någon ANNAN kvarvarande ändring hör till den
      const kvar = historik.filter(x => x.id !== h.id)
      const harStjarnradKvar = kvar.some(r => !KAT_UTAN_STJARNA.includes(r.ny_kategori) && (r.gammal_passtyp !== r.ny_passtyp || r.gammal_kategori !== r.ny_kategori))

      const arBeviljadSemesterRad = (h.notering || '').startsWith('Beviljad semester (ansökan')
      const uppdatering = {
        passtyp_id: matchad?.id ?? nu.passtyp_id ?? null,
        passtyp_namn: aterNamn,
        kategori: aterKategori,
        arbetstimmar: aterTimmar,
        har_passandring: harStjarnradKvar,
        uppdaterad_at: new Date().toISOString()
      }
      // Gulmarkering/beviljad semester från en ansökan: koppla loss just den här dagen från ansökan
      // (resten av ansökans period rörs inte)
      if (arBeviljadSemesterRad || nu.gul_ansokan_id) {
        uppdatering.gul_ansokan_id = null
        uppdatering.gul_ursprung_namn = null
        uppdatering.gul_ursprung_kategori = null
        uppdatering.gul_nytt = false
      }
      const { error: updFel } = await supabase.from('pass').update(uppdatering).eq('id', pass.id)
      if (updFel) throw updFel

      const { error: delFel } = await supabase.from('pass_historik').delete().eq('id', h.id)
      if (delFel) throw delFel

      // Justera X/SX om timmarna ändrats
      const gamlaTimmar = nu.kategori === 'overtid' ? 0 : (nu.arbetstimmar || 0)
      const nyaTimmar = aterKategori === 'overtid' ? 0 : aterTimmar
      const diff = nyaTimmar - gamlaTimmar
      await justeraXOchSX(nu.medarbetare_id, -diff, nu.datum)

      // Meddela medarbetaren (inte för beviljad semester från ansökan - dessa skickar ingen notis per pass)
      if (!arBeviljadSemesterRad && (nu.passtyp_namn || '') !== aterNamn && nu.medarbetare_id) {
        const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
        await supabase.from('ansokningar').insert({
          typ: 'passandring_notis',
          skapad_av: adminInfo.id,
          skapad_av_namn: adminNamn,
          avdelning: null,
          fran_datum: nu.datum,
          till_datum: nu.datum,
          status: 'beviljad',
          medarbetare_kommentar: null,
          admin_kommentar: 'Ändringen har ångrats.',
          besvarad_av: adminInfo.id,
          besvarad_av_namn: adminNamn,
          besvarad_at: new Date().toISOString(),
          mottagare_id: nu.medarbetare_id,
          passandring_fran: nu.passtyp_namn || '–',
          passandring_till: aterNamn,
          passandring_datum: nu.datum
        })
      }
      onSave()
    } catch (err) {
      console.error('Ångra ändring:', err)
      alert('Kunde inte ångra: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  // Gulmarkering från väntande ansökan har ingen historikrad - ångra den här dagen direkt
  useEffect(() => {
    let avbruten = false
    if (!pass?.id) { setGulKoppling(null); return }
    supabase.from('pass').select('id, gul_ansokan_id, gul_ursprung_namn, gul_ursprung_kategori, gul_nytt, kategori, arbetstimmar, medarbetare_id, datum')
      .eq('id', pass.id).single().then(({ data }) => {
        if (!avbruten) setGulKoppling(data?.gul_ansokan_id && data.kategori === 'semester' ? data : null)
      })
    return () => { avbruten = true }
  }, [pass?.id])

  async function angraGulmarkering() {
    const g = gulKoppling
    if (!g) return
    setLoading(true)
    try {
      if (g.gul_nytt) {
        // Dagen var helt ledig från början (inget pass): ta bort passraden helt
        await supabase.from('pass_historik').delete().eq('pass_id', g.id)
        const { data: borta } = await supabase.from('pass').delete().eq('id', g.id).select('id')
        if (!borta || borta.length === 0) {
          // Fick inte radera (behörighet): töm passet i stället
          const { error } = await supabase.from('pass').update({
            passtyp_id: null, passtyp_namn: '', kategori: 'ordinarie', arbetstimmar: 0,
            har_passandring: false, gul_ansokan_id: null, gul_ursprung_namn: null,
            gul_ursprung_kategori: null, gul_nytt: false, uppdaterad_at: new Date().toISOString()
          }).eq('id', g.id)
          if (error) throw error
        }
      } else {
        const namn = g.gul_ursprung_namn || ''
        const matchad = passtyper.find(p => (p.namn || '').toUpperCase() === namn.toUpperCase())
        const { error } = await supabase.from('pass').update({
          passtyp_id: matchad?.id ?? null,
          passtyp_namn: namn,
          kategori: g.gul_ursprung_kategori || 'ordinarie',
          arbetstimmar: matchad?.arbetstimmar ?? g.arbetstimmar ?? 0,
          gul_ansokan_id: null, gul_ursprung_namn: null, gul_ursprung_kategori: null, gul_nytt: false,
          uppdaterad_at: new Date().toISOString()
        }).eq('id', g.id)
        if (error) throw error
      }
      onSave()
    } catch (err) {
      console.error('Ångra gulmarkering:', err)
      alert('Kunde inte ångra: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  // Ingen veckovila: V blir ett vanligt D (samma tider = 0 timmar att registrera, så
  // ingen skiftersättning), loggas i historiken och medarbetaren får en notis.
  async function ingenVeckovila() {
    if (!arVeckovila) return
    setLoading(true)
    try {
      const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
      const dTyp = passtyper.find(p => (p.namn || '').toUpperCase().trim() === 'D')
      if (!dTyp) throw new Error('Hittade ingen passtyp D')
      const dTimmar = dTyp.arbetstimmar || 0

      const { error: updFel } = await supabase.from('pass').update({
        passtyp_id: dTyp.id,
        passtyp_namn: 'D',
        kategori: 'ordinarie',
        arbetstimmar: dTimmar,
        har_passandring: true,
        // Om dagen var gulmarkerad/beviljad från en ansökan kopplas den dagen loss
        gul_ansokan_id: null, gul_ursprung_namn: null, gul_ursprung_kategori: null, gul_nytt: false,
        uppdaterad_at: new Date().toISOString()
      }).eq('id', pass.id)
      if (updFel) throw updFel

      await supabase.from('pass_historik').insert({
        pass_id: pass.id,
        andrad_av: adminInfo.id,
        andrad_av_namn: adminNamn,
        gammal_passtyp: pass.passtyp_namn || 'V',
        ny_passtyp: 'D',
        gammal_kategori: pass.kategori || 'ordinarie',
        ny_kategori: 'ordinarie',
        notering: 'Ingen veckovila (ledighet/passbyte)',
        andrad_at: new Date().toISOString()
      })

      await justeraXOchSX(pass.medarbetare_id, 0, pass.datum)

      if (pass.medarbetare_id) {
        await supabase.from('ansokningar').insert({
          typ: 'passandring_notis',
          skapad_av: adminInfo.id,
          skapad_av_namn: adminNamn,
          avdelning: null,
          fran_datum: pass.datum,
          till_datum: pass.datum,
          status: 'beviljad',
          medarbetare_kommentar: null,
          admin_kommentar: 'Du är inte berättigad till veckovila på grund av ledighet/passbyte.',
          besvarad_av: adminInfo.id,
          besvarad_av_namn: adminNamn,
          besvarad_at: new Date().toISOString(),
          mottagare_id: pass.medarbetare_id,
          passandring_fran: pass.passtyp_namn || 'V',
          passandring_till: 'D',
          passandring_datum: pass.datum
        })
      }
      onSave()
    } catch (err) {
      console.error('Ingen veckovila:', err)
      alert('Kunde inte ändra: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  // Räknar om DENNA X-dag: periodens krav minus alla andra pass i perioden
  // minus timmarna på eventuella andra X-dagar i samma period (de rörs inte).
  async function raknaOmDennaXdag() {
    if (!arXdag) return
    setXFel('')
    setLoading(true)
    try {
      const { data: med, error: medFel } = await supabase.from('medarbetare')
        .select('avdelning, schema_typ').eq('id', pass.medarbetare_id).single()
      if (medFel || !med) throw new Error('Hittade inte medarbetaren')

      const { data: per, error: perFel } = await supabase.from('perioder')
        .select('startdatum, slutdatum, antal_veckor')
        .eq('avdelning', med.avdelning)
        .lte('startdatum', pass.datum).gte('slutdatum', pass.datum)
        .maybeSingle()
      if (perFel || !per || !per.antal_veckor) throw new Error('Hittade ingen period för dagen')

      const kravTimmar = (per.antal_veckor === 8 || med.schema_typ === '40h')
        ? per.antal_veckor * 40 : per.antal_veckor * 34.5

      const { data: allaPass, error: passFel } = await supabase.from('pass')
        .select('id, passtyp_namn, arbetstimmar, kategori')
        .eq('medarbetare_id', pass.medarbetare_id)
        .gte('datum', per.startdatum).lte('datum', per.slutdatum)
        .limit(1000)
      if (passFel) throw passFel

      const arBalans = p => ['X','SX','LX','SJX'].includes((p.passtyp_namn || '').toUpperCase().trim())
      const ovriga = (allaPass || [])
        .filter(p => !arBalans(p) && (p.passtyp_namn || '').trim() !== '' && p.kategori !== 'overtid')
        .reduce((s, p) => s + (p.arbetstimmar || 0), 0)
      const andraX = (allaPass || [])
        .filter(p => arBalans(p) && p.id !== pass.id)
        .reduce((s, p) => s + (p.arbetstimmar || 0), 0)

      const xTimmar = Math.round((kravTimmar - ovriga - andraX) * 10) / 10
      if (xTimmar < 0) throw new Error('Uträkningen blev negativ (' + xTimmar + ' h). Inget ändrades.')

      const { error: updFel } = await supabase.from('pass')
        .update({ arbetstimmar: xTimmar, uppdaterad_at: new Date().toISOString() })
        .eq('id', pass.id)
      if (updFel) throw updFel
      onSave()
    } catch (err) {
      console.error('Räkna om X-dag:', err)
      setXFel('Kunde inte räkna om: ' + (err?.message || 'okänt fel'))
    } finally {
      setLoading(false)
    }
  }

  async function taBortPass() {
    if (!pass.id) return
    setLoading(true)
    try {
      const adminNamn = `${adminInfo.fornamn} ${adminInfo.efternamn}`
      const gamlaTimmar = pass.kategori === 'overtid' ? 0 : (pass.arbetstimmar || 0)

      await supabase.from('pass').update({
        passtyp_id: null, passtyp_namn: '', kategori: 'passbyte',
        arbetstimmar: 0, har_passandring: true,
        uppdaterad_at: new Date().toISOString()
      }).eq('id', pass.id)

      await supabase.from('pass_historik').insert({
        pass_id: pass.id, andrad_av: adminInfo.id, andrad_av_namn: adminNamn,
        gammal_passtyp: pass.passtyp_namn, ny_passtyp: '(borttaget)',
        gammal_kategori: pass.kategori, ny_kategori: 'passbyte',
        notering: 'Pass borttaget – passbyte'
      })

      await justeraXOchSX(pass.medarbetare_id, gamlaTimmar, pass.datum)
      onSave()
    } catch (err) {
      console.error('Fel:', err)
      alert('Fel: ' + (err?.message || JSON.stringify(err)))
    } finally {
      setLoading(false)
    }
  }

  async function justeraXOchSX(medarbetareId, diff, passDatum) {
    try {
      // VIKTIGT: slå upp vilken PERIOD passets datum tillhör, och justera
      // BARA X/SX-dagar inom den perioden - annars påverkas av misstag
      // X-dagar i helt andra, orelaterade perioder varje gång ett enda
      // pass ändras (detta var en allvarlig, tyst bugg tidigare).
      let periodStart = null, periodSlut = null
      if (passDatum) {
        const { data: matchandePeriod } = await supabase
          .from('perioder')
          .select('startdatum, slutdatum')
          .eq('avdelning', avdelning)
          .lte('startdatum', passDatum)
          .gte('slutdatum', passDatum)
          .maybeSingle()
        if (matchandePeriod) {
          periodStart = matchandePeriod.startdatum
          periodSlut = matchandePeriod.slutdatum
        }
      }
      if (!periodStart || !periodSlut) {
        // Hittade ingen period att avgränsa mot - avbryt hellre än att
        // riskera att justera fel period.
        console.warn('X-justering: kunde inte hitta period för datum', passDatum, '- avbryter justering')
        return
      }

      // Räkna om X-dagen från grunden: periodens krav minus alla andra pass i perioden.
      // (Tidigare justerades den bara med skillnaden i timmar, vilket blev fel när t.ex. ett
      // vanligt pass byttes till X - då är skillnaden 0 trots att X-dagen ska räknas om.)
      const { data: medInfo } = await supabase.from('medarbetare').select('schema_typ').eq('id', medarbetareId).single()
      const { data: perInfo } = await supabase.from('perioder').select('antal_veckor')
        .eq('avdelning', avdelning).lte('startdatum', passDatum).gte('slutdatum', passDatum).maybeSingle()
      const veckor = perInfo?.antal_veckor
      if (!veckor) return
      const kravTimmar = (veckor === 8 || medInfo?.schema_typ === '40h') ? veckor * 40 : veckor * 34.5

      const { data: xPasser } = await supabase.from('pass')
        .select('id, passtyp_namn, arbetstimmar, kategori')
        .eq('medarbetare_id', medarbetareId)
        .gte('datum', periodStart)
        .lte('datum', periodSlut)
      const arBalans = p => ['X','SX','LX','SJX'].includes((p.passtyp_namn || '').toUpperCase().trim())
      const ovriga = (xPasser || [])
        .filter(p => !arBalans(p) && (p.passtyp_namn || '').trim() !== '' && p.kategori !== 'overtid')
        .reduce((sum, p) => sum + (p.arbetstimmar || 0), 0)
      const xTimmar = Math.round((kravTimmar - ovriga) * 10) / 10
      const balansIds = (xPasser || []).filter(arBalans).map(p => p.id)
      if (balansIds.length > 0) {
        await supabase.from('pass').update({ arbetstimmar: xTimmar }).in('id', balansIds)
      }
    } catch (err) {
      console.error('X-justering fel:', err)
    }
  }

  return (
    <>
    <div
      className="modal-overlay"
      style={{ backdropFilter: 'none', background: 'rgba(0,0,0,.12)' }}
      onMouseDown={e => { overlayMouseDownRef.current = (e.target === e.currentTarget) }}
      onClick={e => { if (e.target === e.currentTarget && overlayMouseDownRef.current) onClose() }}
    >
      <div className="modal-box" style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}>
        <div
          className="modal-header"
          onMouseDown={startaDrag}
          style={{ cursor: 'move', userSelect: 'none', position: 'sticky', top: 0, zIndex: 5, background: '#fff' }}
        >
          <h3>✏️ Redigera pass – {pass?.medarbetare_namn} · {new Date(pass?.datum).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <label className="form-label">Passtyp</label>
          <select className="form-control" value={valdPasstyp} onChange={e => { setValdPasstyp(e.target.value); setPasstypAndrat(true) }}>
            <option value="">— Välj passtyp —</option>
            {passtyper
              .filter(p => !avdelning || !p.avdelningar || p.avdelningar.length === 0 || p.avdelningar.includes(avdelning) || p.id === valdPasstyp)
              .map(p => (
                <option key={p.id} value={p.id}>{p.namn} – {p.starttid}–{p.sluttid} ({p.arbetstimmar}h)</option>
              ))}
          </select>

          {!erOvertid && (
            <>
              <label className="form-label">Status / kategori</label>
              <select className="form-control" value={valdKategori} onChange={e => { setValdKategori(e.target.value); setStatusAndrat(true) }}>
                <option value="ordinarie">Ordinarie</option>
                <option value="passbyte">Passbyte</option>
                <option value="semester">Semester (S läggs till)</option>
                <option value="beviljad_semester">Beviljad semester ✦</option>
                <option value="foraldraledighet">Föräldraledighet</option>
                <option value="beviljad_foraldraledighet">Beviljad föräldraledighet ✦</option>
                <option value="permission">Permission (L läggs till)</option>
                <option value="flex">Flex (L läggs till)</option>
                <option value="ledig">Betald ledig dag (L läggs till, grå ruta)</option>
                <option value="sjukdom">Sjukdom</option>
              </select>
            </>
          )}

          {arVeckovila && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setVisaIngenVila(true)} disabled={loading}
              style={{ width:'100%', margin:'0 0 4px', fontSize:'.8rem' }}>
              Ingen veckovila (blir D)
            </button>
          )}
          {arVeckovila && (
            <p style={{ margin:'0 0 10px', fontSize:'.74rem', color:'#888', fontStyle:'italic' }}>
              Passet blir D och medarbetaren får en notis om att veckovilan uteblir på grund av ledighet/passbyte.
            </p>
          )}

          {arXdag && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={raknaOmDennaXdag} disabled={loading}
              style={{ width:'100%', margin:'0 0 4px', fontSize:'.8rem' }}>
              Räkna om X-dagen
            </button>
          )}
          {arXdag && (
            <p style={{ margin:'0 0 10px', fontSize:'.74rem', color: xFel ? '#c62828' : '#888', fontStyle: xFel ? 'normal' : 'italic' }}>
              {xFel || 'Räknar ut X-dagens timmar från periodens krav minus övriga pass. Andra X-dagar i perioden ändras inte.'}
            </p>
          )}

          <div style={{ display:'flex', alignItems:'center', gap:8, margin:'10px 0', padding:'10px 12px', background: erOvertid?'#fff3e0':'#f9f9f9', borderRadius:6, border:`1.5px solid ${erOvertid?'#e65100':'#e8e8e8'}` }}>
            <input type="checkbox" id="overtid-check" checked={erOvertid} onChange={e => { setErOvertid(e.target.checked); setStatusAndrat(true) }} style={{ width:16, height:16, cursor:'pointer' }} />
            <label htmlFor="overtid-check" style={{ cursor:'pointer', fontWeight: erOvertid?700:400, color: erOvertid?'#e65100':'#555', fontSize:'.88rem' }}>
              🟠 Övertid (ÖT) – timmar räknas inte
            </label>
          </div>

          <div style={{ padding:'10px 12px', margin:'0 0 10px', background: harAtf?'#e3f2fd':'#f9f9f9', borderRadius:6, border:`1.5px solid ${harAtf?'#1565c0':'#e8e8e8'}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="atf-check" checked={harAtf} onChange={e => setHarAtf(e.target.checked)} style={{ width:16, height:16, cursor:'pointer' }} />
              <label htmlFor="atf-check" style={{ cursor:'pointer', fontWeight: harAtf?700:400, color: harAtf?'#1565c0':'#555', fontSize:'.88rem' }}>
                ⏱ ATF registrerat
              </label>
            </div>
            {harAtf && (
              <div className="form-row" style={{ marginTop: 10, marginBottom: 0 }}>
                <div><label className="form-label">Från</label><input className="form-control" type="time" value={atfFran} onChange={e => setAtfFran(e.target.value)} style={{ marginBottom: 0 }} /></div>
                <div><label className="form-label">Till</label><input className="form-control" type="time" value={atfTill} onChange={e => setAtfTill(e.target.value)} style={{ marginBottom: 0 }} /></div>
              </div>
            )}
          </div>

          <div style={{ padding:'10px 12px', margin:'0 0 10px', background: harBeredskap?'#fdecea':'#f9f9f9', borderRadius:6, border:`1.5px solid ${harBeredskap?'#c0392b':'#e8e8e8'}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="beredskap-check" checked={harBeredskap} onChange={e => setHarBeredskap(e.target.checked)} style={{ width:16, height:16, cursor:'pointer' }} />
              <label htmlFor="beredskap-check" style={{ cursor:'pointer', fontWeight: harBeredskap?700:400, color: harBeredskap?'#c0392b':'#555', fontSize:'.88rem' }}>
                🔴 Beredskap
              </label>
            </div>
          </div>

          <div style={{ padding:'10px 12px', margin:'0 0 10px', background: harBokaDag?'#e3f2fd':'#f9f9f9', borderRadius:6, border:`1.5px solid ${harBokaDag?'#1565c0':'#e8e8e8'}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" id="bokadag-check" checked={harBokaDag} onChange={e => setHarBokaDag(e.target.checked)} style={{ width:16, height:16, cursor:'pointer' }} />
              <label htmlFor="bokadag-check" style={{ cursor:'pointer', fontWeight: harBokaDag?700:400, color: harBokaDag?'#1565c0':'#555', fontSize:'.88rem' }}>
                🔵 Bokad dag
              </label>
            </div>
          </div>

          <label className="form-label">Kommentar (sparas bara i historiken)</label>
          <textarea className="form-control" rows={2} placeholder="T.ex. anledning till ändringen..." value={kommentar} onChange={e => setKommentar(e.target.value)} />

          <label className="form-label">Ändringsdatum (valfritt)</label>
          <input type="date" className="form-control" value={andringsdatum} max={idagStr} onChange={e => setAndringsdatum(e.target.value)} style={andringsdatumFramtid ? { borderColor: '#c62828' } : undefined} />
          {andringsdatumFramtid && (
            <p style={{ margin:'-4px 0 8px', fontSize:'.78rem', color:'#c62828', fontWeight:600 }}>Ändringsdatum kan inte vara i framtiden. Välj dagens datum eller tidigare.</p>
          )}
          <p style={{ margin:'-4px 0 12px', fontSize:'.76rem', color:'#999' }}>
            Lämna tomt för att logga ändringen som gjord idag (normalt). Fyll bara i om du registrerar en ändring som egentligen redan skedde tidigare (t.ex. från en gammal Excel-fil) – styr då vilket datum skiftersättningen räknas utifrån, och markerar passet med gul stjärna.
          </p>

          {andringsdatum && (
            <>
              <label className="form-label">Tidigare passtyp (för historiken)</label>
              <input className="form-control" placeholder="T.ex. D" value={gammalPassManuell} onChange={e => setGammalPassManuell(e.target.value.toUpperCase())} />
              <p style={{ margin:'-4px 0 12px', fontSize:'.76rem', color:'#999' }}>
                Vad passet var INNAN den historiska ändringen (t.ex. "D" om det ändrades till nuvarande "{pass?.passtyp_namn || ''}"). Behövs för att historiken ska visa rätt "från → till".
              </p>
            </>
          )}

          <label className="form-label">Notering som visas i schemat</label>
          <textarea className="form-control" rows={2} placeholder="T.ex. Extra möte 10-12..." value={synligNotering} onChange={e => setSynligNotering(e.target.value)} />
          <p style={{ margin:'-4px 0 12px', fontSize:'.76rem', color:'#999' }}>
            Visas automatiskt i schemat så länge fältet inte är tomt.
          </p>

          <label className="form-label">Historik</label>
          <div className="history-box">
            {historik.length === 0 ? <span style={{ color:'#bbb' }}>Inga tidigare ändringar</span> : (
              historik.map(h => (
                <div key={h.id} className="history-entry">
                  <div>
                    <strong>{h.andrad_av_namn}</strong>
                    {h.gammal_passtyp !== h.ny_passtyp && <> – {h.gammal_passtyp} → {h.ny_passtyp}</>}
                    {h.gammal_kategori !== h.ny_kategori && <> · {KATEGORI_FARGER[h.ny_kategori]?.label}</>}
                    {h.notering && <> · "{h.notering}"</>}
                  </div>
                  <div className="history-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{new Date(h.andrad_at).toLocaleString('sv-SE')}</span>
                    {senasteAndring?.id === h.id && (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={loading}
                        onClick={() => setAngraRad(h)}>↩ Ångra denna ändring</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {viloVarning && (
          <div style={{ background:'#fff3cd', border:'1px solid #ffc107', borderRadius:6, padding:'12px 16px', margin:'0 16px 12px' }}>
            <div style={{ fontWeight:700, marginBottom:6, color:'#856404' }}>⚠️ Vilobrott – vill du fortsätta?</div>
            {viloVarning.map((v, i) => <div key={i} style={{ fontSize:'.82rem', color:'#856404', marginBottom:3 }}>• {v}</div>)}
            <div style={{ fontSize:'.75rem', color:'#856404', marginTop:6, fontStyle:'italic' }}>
              Observera: vid beredskap gäller 8 timmars dygnsvila istället för 11.
            </div>
            <div style={{ marginTop:10, display:'flex', gap:8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setViloVarning(null)}>✕ Avbryt</button>
              <button className="btn btn-danger btn-sm" onClick={async () => { setViloVarning(null); setLoading(true); await genomforSpara() }}>Spara ändå →</button>
            </div>
          </div>
        )}

        <div className="modal-footer" style={{ justifyContent:'space-between' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'stretch' }}>
            {gulKoppling && (
              <button className="btn btn-secondary" onClick={() => setVisaAngraGul(true)} disabled={loading} style={{ fontSize:'.8rem', whiteSpace:'nowrap' }}>
                Ta bort gulmarkering
              </button>
            )}
            {pass.id && (
              <button className="btn btn-danger" onClick={() => setVisaTaBortDialog(true)} disabled={loading} style={{ fontSize:'.8rem', whiteSpace:'nowrap' }}>
                Ta bort pass
              </button>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Avbryt</button>
            <button className="btn btn-primary" onClick={handleSpara} disabled={loading || andringsdatumFramtid}>
              {loading ? 'Sparar...' : '💾 Spara'}
            </button>
          </div>
        </div>
      </div>
    </div>
    {angraRad && (
      <BekraftaModal
        titel="Ångra ändring"
        meddelande={!angraRad.gammal_passtyp ? 'Ta bort passet så att dagen blir ledig igen?\n\nLoggraden tas bort.' : `Sätt tillbaka passet till ${angraRad.gammal_passtyp}${angraRad.gammal_kategori && angraRad.gammal_kategori !== 'ordinarie' ? ` (${KATEGORI_FARGER[angraRad.gammal_kategori]?.label || angraRad.gammal_kategori})` : ''}?\n\nLoggraden tas bort, och ersättning som hör till den här ändringen försvinner med den.`}
        onBekrafta={() => { const h = angraRad; setAngraRad(null); angraSenasteAndring(h) }}
        onAvbryt={() => setAngraRad(null)}
      />
    )}
    {visaAngraGul && (
      <BekraftaModal
        titel="Ta bort gulmarkering"
        meddelande="Sätt tillbaka just det här passet till hur det var före gulmarkeringen? Resten av ansökans dagar rörs inte."
        onBekrafta={() => { setVisaAngraGul(false); angraGulmarkering() }}
        onAvbryt={() => setVisaAngraGul(false)}
      />
    )}
    {visaIngenVila && (
      <BekraftaModal
        titel="Ingen veckovila"
        meddelande="Ändra V till D? Medarbetaren får en notis om att hen inte är berättigad till veckovila på grund av ledighet/passbyte. Går att ångra under Historik."
        onBekrafta={() => { setVisaIngenVila(false); ingenVeckovila() }}
        onAvbryt={() => setVisaIngenVila(false)}
      />
    )}
    {visaTaBortDialog && (
      <BekraftaModal
        titel="Ta bort pass"
        meddelande="Ta bort detta pass? Åtgärden kan inte ångras."
        farligKnapp
        onBekrafta={() => { setVisaTaBortDialog(false); taBortPass() }}
        onAvbryt={() => setVisaTaBortDialog(false)}
      />
    )}
    </>
  )
}
